// What the client has seen of players stopping looking, with when it saw it.
//
// The room counts (chat/room_counts) and each member's userlist "searching"
// flag are snapshots that can be a minute or more old. The open-searches feed
// and the current match are live. This remembers, per player and game, when a
// search the feed showed ended (removed, became a match, or left the feed) and
// when a player turned up in your match, so a snapshot older than that can be
// corrected. Players the client never saw searching are left alone.

const ANY_LADDER = 'any';

// searchId -> { playerId, ladderId } for the searches live at the last look.
let liveSearches = new Map();
// playerId -> Map(ladderId -> time the feed saw their search end)
let searchEnds = new Map();
// playerId -> time they were first seen in your current match
let matchSightings = new Map();
let seenMatchIds = new Set();
// Searches the feed still lists although they became your match.
let supersededSearchIds = new Set();
let version = 0;

function ladderKey(ladderId) {
	return ladderId === null || ladderId === undefined ? ANY_LADDER : String(ladderId);
}

function isLive(search) {
	return !!search && !!search.isSearch && !search.is_removed && !!search.player1;
}

function hasLiveSearch(playerId, ladderId) {
	for(const live of liveSearches.values()) {
		if(String(live.playerId) !== String(playerId)) {
			continue;
		}
		if(ladderId === null || ladderId === undefined || live.ladderKey === ladderKey(ladderId)) {
			return true;
		}
	}
	return false;
}

function lastSearchEnd(playerId, ladderId) {
	const ends = searchEnds.get(String(playerId));
	if(!ends) {
		return 0;
	}
	if(ladderId === null || ladderId === undefined) {
		return Math.max(0, ...ends.values());
	}
	return ends.get(ladderKey(ladderId)) || 0;
}

/** Compare the feed's searches with the last look and note the ones that ended. */
export function observeSearchFeed(searches, now = Date.now()) {
	const nextLive = new Map();
	for(const search of searches) {
		if(isLive(search) && !supersededSearchIds.has(String(search.id))) {
			nextLive.set(String(search.id), {
				playerId: String(search.player1.id),
				ladderKey: ladderKey(search.ladder ? search.ladder.id : null),
			});
		}
	}
	let changed = false;
	for(const [searchId, ended] of liveSearches) {
		if(nextLive.has(searchId)) {
			continue;
		}
		recordSearchEnd(ended.playerId, ended.ladderKey, now);
		changed = true;
	}
	for(const [searchId, live] of nextLive) {
		if(liveSearches.has(searchId)) {
			continue;
		}
		changed = true;
		const ends = searchEnds.get(live.playerId);
		if(ends) {
			ends.delete(live.ladderKey);
		}
	}
	liveSearches = nextLive;
	if(changed) {
		version++;
	}
}

function recordSearchEnd(playerId, searchLadderKey, now) {
	if(!searchEnds.has(playerId)) {
		searchEnds.set(playerId, new Map());
	}
	searchEnds.get(playerId).set(searchLadderKey, now);
}

function playersIn(match) {
	const players = [match.player1, match.player2];
	if(match.accepted_players) {
		players.push(...match.accepted_players.values());
	}
	if(match.players) {
		Object.values(match.players).forEach((entry) => players.push(entry && entry.player));
	}
	return players.filter((player) => player && player.id !== undefined && player.id !== null);
}

/**
 * Everyone in your match stopped looking when it started. Their searches the
 * feed showed until then ended, even if the feed keeps listing them.
 */
export function observeCurrentMatch(match, now = Date.now()) {
	if(!match || !match.id || seenMatchIds.has(String(match.id))) {
		return;
	}
	seenMatchIds.add(String(match.id));
	const playerIds = new Set(playersIn(match).map((player) => String(player.id)));
	playerIds.forEach((playerId) => matchSightings.set(playerId, now));
	for(const [searchId, live] of liveSearches) {
		if(playerIds.has(live.playerId)) {
			supersededSearchIds.add(searchId);
			liveSearches.delete(searchId);
			recordSearchEnd(live.playerId, live.ladderKey, now);
		}
	}
	version++;
}

/** Whether the feed still lists this search although it became your match. */
export function isSupersededSearch(searchId) {
	return supersededSearchIds.has(String(searchId));
}

/**
 * Whether the client saw the player stop looking on the game (null: any game)
 * after `since`, and has not seen them start again.
 */
export function stoppedLookingAfter(playerId, ladderId, since) {
	if(hasLiveSearch(playerId, ladderId)) {
		return false;
	}
	const stoppedAt = Math.max(lastSearchEnd(playerId, ladderId), matchSightings.get(String(playerId)) || 0);
	return stoppedAt > since;
}

function isKnownMember(chatRoom, playerId) {
	if(typeof myUser !== 'undefined' && myUser && String(myUser.id) === String(playerId)) {
		return true; // The room list only has rooms you are in.
	}
	const userlist = chatRoom.userlist;
	return !!userlist && typeof userlist.hasUser === 'function' && userlist.hasUser(playerId);
}

/**
 * Members of the room the feed saw stop looking on its game (any game in a
 * room without one) after `since`. A room count from before then still
 * counts them. Only searches the feed showed end count: being in a match
 * says nothing about whether the player was counted as looking.
 */
export function countMembersStoppedLookingSince(chatRoom, since) {
	const ladderId = chatRoom.ladder ? chatRoom.ladder.id : null;
	let count = 0;
	for(const playerId of searchEnds.keys()) {
		if(!isKnownMember(chatRoom, playerId) || hasLiveSearch(playerId, ladderId)) {
			continue;
		}
		if(lastSearchEnd(playerId, ladderId) > since) {
			count++;
		}
	}
	return count;
}

/** Changes whenever a search ends or starts, or a new match is seen. */
export function searchActivityVersion() {
	return version;
}

export function resetSearchActivity() {
	liveSearches = new Map();
	searchEnds = new Map();
	matchSightings = new Map();
	seenMatchIds = new Set();
	supersededSearchIds = new Set();
	version = 0;
}
